import { useId, useMemo, useState } from "react";
import { defaults, estimate } from "@/lib/roi-model";
import { useSite } from "@/context/SiteContext";

const usd = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
const UTILITIES = ["FPL", "Duke Energy Florida", "TECO", "JEA", "OUC", "Other / not sure"];
const ROOFS = ["TPO", "Standing Seam Metal", "Built-Up", "Other / not sure"];

/** Florida cash-ownership screening estimator (shared roi-model). Ungated; no fake lead endpoint. */
export default function FloridaEstimator({ initialUtility = "FPL", sourceCheckDate = null }) {
  const id = useId();
  const { openAudit } = useSite();
  const [input, setInput] = useState({ ...defaults, utility: initialUtility });
  const [error, setError] = useState("");
  const result = useMemo(() => {
    try { return { value: estimate(input) }; } catch (e) { return { error: e.message }; }
  }, [input]);

  const field = (key, label, min, max, step = 1, testid) => (
    <label htmlFor={id + key} className="sbb-field">
      <span>{label}</span>
      <input id={id + key} type="number" value={input[key]} min={min} max={max} step={step}
        data-testid={testid} onChange={(e) => setInput({ ...input, [key]: Number(e.target.value) })} />
    </label>
  );

  function requestReport() {
    setError("");
    const r = result.value;
    if (!r) { setError("Fix the highlighted inputs first."); return; }
    openAudit({
      originPath: window.location.pathname, utility: input.utility, roofType: input.roofType,
      buildingSqFt: input.buildingSqFt,
      scenario: {
        systemKW: r.systemKW, grossCost: r.grossCost, npv25: r.npv25,
        paybackYears: r.paybackYears, taxScheduleConfirmed: input.taxScheduleConfirmed,
        utility: input.utility, sourceCheckDate, modelVersion: "florida-cash-v1",
      },
    });
  }

  return (
    <section aria-labelledby={`${id}title`} className="fl-estimator" data-testid="florida-estimator">
      <h2 id={`${id}title`}>What could solar change for your business?</h2>
      <p>Explore a free cash-ownership scenario. Send a bill for a preliminary financial review — the estimate itself is ungated.</p>
      <div className="fl-estimator-fields">
        {field("monthlySpend", "Monthly electricity spend ($)", 1, 10000000, 1, "fl-spend")}
        {field("buildingSqFt", "Building square footage", 1, 100000000, 1, "fl-building")}
        <label htmlFor={`${id}utility`} className="sbb-field">
          <span>Utility</span>
          <select id={`${id}utility`} value={input.utility} data-testid="fl-utility" onChange={(e) => setInput({ ...input, utility: e.target.value })}>
            {UTILITIES.map((x) => <option key={x}>{x}</option>)}
          </select>
        </label>
        <label htmlFor={`${id}roof`} className="sbb-field">
          <span>Roof type</span>
          <select id={`${id}roof`} value={input.roofType} data-testid="fl-roof" onChange={(e) => setInput({ ...input, roofType: e.target.value })}>
            {ROOFS.map((x) => <option key={x}>{x}</option>)}
          </select>
        </label>
      </div>
      <p className="micro">Utility and roof selections help route your review. Rates and roof suitability are not inferred from these selections.</p>
      <details className="sbb-assumptions" data-testid="fl-assumptions">
        <summary>View and edit scenario assumptions</summary>
        <div className="fl-estimator-fields">
          {field("usableRoofFraction", "Usable roof fraction (0–1)", 0, 1, 0.05, "fl-roof-fraction")}
          {field("annualYield", "Illustrative annual yield (kWh/kW)", 1, 2500, 10, "fl-yield")}
          {field("importRate", "Energy-only purchase rate ($/kWh)", 0.001, 2, 0.001, "fl-rate")}
          {field("energyBillFraction", "Energy share of bill (0–1)", 0, 1, 0.05, "fl-energy-share")}
          {field("selfUseFraction", "Onsite production share (0–1)", 0, 1, 0.05, "fl-self-use")}
          {field("dollarsPerWatt", "Installed solar price ($/W)", 0.01, 20, 0.1, "fl-dollar-watt")}
          {field("discountRate", "Discount rate (decimal)", 0, 1, 0.01, "fl-discount")}
          {field("taxRate", "Effective marginal tax rate (decimal)", 0, 1, 0.01, "fl-taxrate-input")}
        </div>
        <p className="sbb-hint">Demonstration assumptions: 12 W per usable sq ft; 1.5% annual maintenance; 0.5% annual degradation; 2% annual energy/maintenance escalation. No applied credit, depreciation, demand reduction, replacement cost or export revenue. A reviewed report must include project-specific replacements and tax timing.</p>
      </details>
      {result.error ? (
        <p role="alert" className="sbb-error" data-testid="fl-error">{result.error}</p>
      ) : (
        <>
          <dl aria-live="polite" className="fl-estimator-results" data-testid="fl-results">
            <div><dt>Illustrative system size</dt><dd data-testid="fl-kw">{result.value.systemKW.toFixed(1)} kW DC</dd></div>
            <div><dt>Year-1 tax benefits applied</dt><dd data-testid="fl-tax">{usd(result.value.yearOneTaxShield)}</dd></div>
            <div><dt>25-year cash NPV at 8%</dt><dd data-testid="fl-npv">{usd(result.value.npv25)}</dd></div>
            <div><dt>Cumulative cash payback</dt><dd data-testid="fl-payback">{result.value.paybackYears === null ? "Not reached in 25 years" : `${result.value.paybackYears.toFixed(1)} years`}</dd></div>
          </dl>
          <details className="sbb-reading" data-testid="fl-warnings">
            <summary>Model assumptions &amp; honest limits</summary>
            <ul>{result.value.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </details>
          <p className="micro">Tax benefits start at zero pending project review. This estimate is not a quote, tariff simulation or guaranteed return.</p>
          <button type="button" className="sbb-primary" onClick={requestReport} data-testid="fl-request-report">Get My Bill-Based ROI Review</button>
        </>
      )}
      {error && <p role="alert" className="sbb-error" data-testid="fl-request-error">{error}</p>}
    </section>
  );
}
